import { useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { LocalizationProvider } from "@/lib/i18n";
import { ConfirmProvider } from "@/components/confirm-dialog";
import { Toaster } from "@/components/toaster";
import { TooltipLayer } from "@/components/tooltip-layer";
import { createQueryClient } from "@/lib/query/client";
import { ServiceQualityTelemetry } from "@/lib/service-quality-telemetry";
import "@/app/globals.css";
import "./root-layout.css";

/**
 * Router-level replacement for the removed src/app/layout.tsx (Next.js App
 * Router root layout). LocalizationProvider defaults to "en" and
 * self-corrects from the locale cookie/localStorage in a client effect, so
 * unlike the Next layout this does not need to resolve the locale
 * server-side before first paint.
 */
export function RootLayout({ children }: { children: ReactNode }) {
  // Held in state rather than module scope so each test render — and each
  // StrictMode remount — gets its own cache instead of inheriting another
  // render's fetched data.
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider>
        <ConfirmProvider>
          <ServiceQualityTelemetry />
          {children}
          <Toaster />
          <TooltipLayer />
        </ConfirmProvider>
      </LocalizationProvider>
    </QueryClientProvider>
  );
}
